import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { Wallet, ArrowRight, AlertCircle, Mail, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from 'sonner';

export default function VerifyOTPPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyOTP, resendOTP } = useAuth();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  
  const email = location.state?.email;

  useEffect(() => {
    if (!email) {
      navigate('/register');
    }
  }, [email, navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleVerify = async () => {
    if (otp.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await verifyOTP(email, otp);
      toast.success('Account verified successfully!');
      navigate('/dashboard');
    } catch (err) {
      const message = err.response?.data?.detail || 'Verification failed. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');

    try {
      await resendOTP(email);
      toast.success('New verification code sent!');
      setCountdown(60);
      setOtp('');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to resend code';
      toast.error(message);
    } finally {
      setResending(false);
    }
  };

  if (!email) return null;

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md space-y-8"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2" data-testid="verify-logo-link">
          <Wallet className="w-8 h-8 text-cyan-400" />
          <span className="text-2xl font-outfit font-bold gradient-text">Dexfolio</span>
        </Link>

        {/* Card */}
        <div className="glass-card p-8 space-y-6">
          {/* Header */}
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-cyan-400" />
            </div>
            <h1 className="text-2xl font-outfit font-bold text-white">Verify Your Email</h1>
            <p className="text-white/60 mt-2">
              We've sent a 6-digit code to
            </p>
            <p className="text-cyan-400 font-medium">{email}</p>
          </div>

          {/* Error Alert */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 p-3 rounded-lg bg-hotpink-400/10 border border-hotpink-400/30 text-hotpink-400"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </motion.div>
          )}

          {/* OTP Input */}
          <div className="flex justify-center">
            <InputOTP 
              maxLength={6} 
              value={otp} 
              onChange={setOtp}
              data-testid="otp-input"
            >
              <InputOTPGroup className="gap-2">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <InputOTPSlot 
                    key={index}
                    index={index} 
                    className="w-12 h-14 text-xl font-mono bg-white/5 border-white/10 text-white rounded-lg"
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>

          {/* Verify Button */}
          <Button 
            onClick={handleVerify}
            className="btn-primary w-full"
            disabled={loading || otp.length !== 6}
            data-testid="verify-submit-btn"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Verifying...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Verify Email
                <ArrowRight className="w-4 h-4" />
              </span>
            )}
          </Button>

          {/* Resend */}
          <div className="text-center">
            <p className="text-white/60 text-sm mb-2">
              Didn't receive the code?
            </p>
            <Button
              variant="ghost"
              onClick={handleResend}
              disabled={resending || countdown > 0}
              className="text-cyan-400 hover:text-cyan-300"
              data-testid="resend-btn"
            >
              {resending ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : countdown > 0 ? (
                `Resend in ${countdown}s`
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Resend Code
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Back to Login */}
        <p className="text-center text-white/60">
          Wrong email?{' '}
          <Link to="/register" className="text-cyan-400 hover:text-cyan-300 transition-colors" data-testid="back-to-register-link">
            Go back to registration
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
